import React, { useState, useRef } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw, CheckCircle, XCircle, Info, Paperclip, X } from 'lucide-react';
import WidgetInfoPopover from './WidgetInfoPopover';
import dashboardService from '../api';

const WIDGET_AGENTS = ['RAG Agent', 'Transcript Analysis Agent (Jamba)'];
const WIDGET_PROMPT = `Stage 1 — RAG Query:
"Based on this conversation excerpt, what fraud typologies are most likely?
Identify relevant fraud signals and patterns: {transcript_excerpt}"

Stage 2 — Transcript Analysis:
You are a Fraud Detection AI Agent performing detailed analysis of a fraud claim call.

CONTEXT FROM KNOWLEDGE BASE: The RAG system has identified these candidate fraud
typologies: {rag_candidates}

RELEVANT FRAUD SIGNALS FROM KNOWLEDGE BASE: {rag_signals}

YOUR TASK: Analyze the full transcript and determine the most likely fraud typology.

OUTPUT FORMAT (JSON):
{
  "primary_typology": { "name", "confidence", "signals_detected", "transcript_evidence" },
  "alternative_typologies": [...],
  "rag_alignment": { "matches_rag_prediction", "rag_candidates_considered", "additional_insights" },
  "red_flags": [...],
  "missing_information": [...],
  "recommended_questions": [...]
}`;

const ConfidenceBadge = ({ score }) => {
  if (score == null) return null;
  const color = score >= 75 ? 'bg-green-100 text-green-700 border-green-200'
    : score >= 50 ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
    : 'bg-red-100 text-red-700 border-red-200';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${color}`}>
      {score}%
    </span>
  );
};

const ConfidenceBar = ({ score }) => {
  const color = score >= 75 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="w-full bg-gray-100 rounded-full h-2">
      <div className={`${color} h-2 rounded-full transition-all duration-500`} style={{ width: `${score}%` }} />
    </div>
  );
};

const FraudTypeIdentifier = ({ result, isLoading, error, onRefresh, onCollectionChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [collection, setCollection] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const fileInputRef = useRef(null);

  const notApplicable = result?.not_applicable === true;
  const fraudData = result?.data;
  const primary = fraudData?.primary_typology;
  const alternatives = fraudData?.alternative_typologies || [];
  const ragAlignment = fraudData?.rag_alignment;
  const redFlags = fraudData?.red_flags || [];
  const missingInfo = fraudData?.missing_information || [];
  const hasData = !!fraudData;

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setAttachedFiles(files);
    setUploadStatus('uploading');
    const collName = `fraud_typology_${Date.now().toString().slice(-8)}`;
    const res = await dashboardService.uploadDocuments(files, collName);
    if (res.success) {
      setCollection(collName);
      setUploadStatus('done');
      if (onCollectionChange) onCollectionChange(collName);
    } else {
      setUploadStatus('error');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFiles = () => {
    setAttachedFiles([]);
    setCollection('');
    setUploadStatus('');
    if (onCollectionChange) onCollectionChange('');
  };

  return (
    <div className="bg-white rounded-xl shadow-md shadow-blue-900/5 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#000080]/5 to-[#000080]/10">
        <div className="flex items-center gap-2">
          <AlertTriangle size={15} style={{ color: '#000080' }} />
          <h3 className="font-semibold text-gray-800 text-sm">Fraud Type Identifier</h3>
          {isLoading && hasData && (
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#000080' }} title="Updating..." />
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => fileInputRef.current?.click()}
            className={`p-1.5 rounded-lg transition-colors ${attachedFiles.length > 0 ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'hover:bg-white/70 text-gray-500 hover:text-gray-700'}`}
            title="Attach fraud typology documents">
            <Paperclip size={14} />
          </button>
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.txt,.csv" className="hidden" onChange={handleFileSelect} />
          <WidgetInfoPopover agents={WIDGET_AGENTS} prompt={WIDGET_PROMPT} />
          <button onClick={onRefresh} disabled={isLoading} className="p-1.5 rounded-lg hover:bg-white/70 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40" title="Re-analyze">
            <RefreshCw size={14} className={isLoading && !hasData ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {attachedFiles.length > 0 && (
        <div className={`px-4 py-2 border-b flex items-center justify-between gap-2 text-xs ${uploadStatus === 'done' ? 'bg-green-50 border-green-100' : uploadStatus === 'error' ? 'bg-red-50 border-red-100' : 'bg-blue-50 border-blue-100'}`}>
          <div className="flex items-center gap-1.5 min-w-0">
            {uploadStatus === 'uploading' && <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin shrink-0" />}
            {uploadStatus === 'done' && <CheckCircle size={12} className="text-green-600 shrink-0" />}
            {uploadStatus === 'error' && <XCircle size={12} className="text-red-500 shrink-0" />}
            <span className={`truncate ${uploadStatus === 'done' ? 'text-green-700' : uploadStatus === 'error' ? 'text-red-600' : 'text-primaryDark'}`}>
              {uploadStatus === 'uploading' ? 'Uploading...' : uploadStatus === 'done' ? `${attachedFiles.length} file(s) ready — ${collection}` : 'Upload failed'}
            </span>
          </div>
          <button onClick={handleRemoveFiles} className="text-gray-400 hover:text-gray-600 shrink-0"><X size={12} /></button>
        </div>
      )}

      <div className="p-4">
        {/* Not a fraud call */}
        {notApplicable && !isLoading && (
          <div className="flex flex-col items-center justify-center py-4 text-gray-400 gap-1">
            <CheckCircle size={22} className="text-gray-300" />
            <p className="text-xs mt-1 text-gray-500">No fraud indicators — conversation is not a fraud claim call</p>
          </div>
        )}

        {/* First-load: no data yet */}
        {!hasData && !notApplicable && !error && (
          <div className="flex flex-col items-center justify-center py-4 text-gray-400 gap-1">
            {isLoading
              ? <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#000080', borderTopColor: 'transparent' }} />
              : <AlertTriangle size={22} className="opacity-30" />}
            <p className="text-xs mt-1">{isLoading ? 'Analyzing...' : 'Waiting for transcript...'}</p>
            {!isLoading && !attachedFiles.length && <p className="text-xs text-gray-400">Attach fraud typology docs for better accuracy</p>}
          </div>
        )}

        {error && !hasData && (
          <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-xs text-red-600">
            <XCircle size={14} className="shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}

        {hasData && (
          <>
            {primary && (
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Primary Classification</span>
                  <ConfidenceBadge score={primary.confidence} />
                </div>
                <div className={`rounded-lg p-3 border ${primary.confidence >= 75 ? 'bg-green-50 border-green-200' : primary.confidence >= 50 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                  <p className="font-semibold text-gray-800 text-sm mb-1.5">{primary.name}</p>
                  <ConfidenceBar score={primary.confidence} />
                </div>
              </div>
            )}

            {ragAlignment && (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
                {ragAlignment.matches_rag_prediction
                  ? <CheckCircle size={13} className="text-green-500 shrink-0" />
                  : <XCircle size={13} className="text-orange-400 shrink-0" />}
                <span className="text-xs text-gray-600">RAG & Analysis {ragAlignment.matches_rag_prediction ? 'agree' : 'diverge'} on classification</span>
              </div>
            )}

            {primary?.signals_detected?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 font-medium mb-1.5 uppercase tracking-wide">Detected Signals</p>
                <div className="flex flex-wrap gap-1.5">
                  {primary.signals_detected.map((sig, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full border" style={{ background: '#000080/10', backgroundColor: 'rgba(0,0,128,0.07)', color: '#000080', borderColor: 'rgba(0,0,128,0.15)' }}>{sig}</span>
                  ))}
                </div>
              </div>
            )}

            {redFlags.length > 0 && (
              <div className="mb-3 p-2.5 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-xs font-semibold text-red-700 mb-1.5 flex items-center gap-1"><AlertTriangle size={12} /> Red Flags</p>
                <ul className="space-y-1">
                  {redFlags.map((flag, i) => (
                    <li key={i} className="text-xs text-red-600 flex items-start gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-red-400 shrink-0 mt-1.5" />{flag}
                    </li>
                  ))}
                </ul>
              </div>
            )}


            <button className="flex items-center gap-1.5 text-xs font-medium hover:opacity-80 transition-opacity mt-1" style={{ color: '#000080' }} onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              {expanded ? 'Hide details' : 'Show details'}
            </button>

            {expanded && (
              <div className="mt-3 space-y-3">
                {primary?.transcript_evidence?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">Transcript Evidence</p>
                    <div className="space-y-2">
                      {primary.transcript_evidence.map((ev, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                          <p className="text-xs text-gray-700 italic mb-1">"{ev.quote}"</p>
                          <div className="flex items-center gap-2">
                            {ev.timestamp && <span className="text-xs text-gray-400">{ev.timestamp}</span>}
                            {ev.signal && <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(0,0,128,0.07)', color: '#000080' }}>{ev.signal}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {alternatives.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">Alternative Typologies</p>
                    <div className="space-y-2">
                      {alternatives.map((alt, i) => (
                        <div key={i} className="flex items-start justify-between gap-2 p-2 bg-gray-50 rounded-lg border border-gray-100">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-700">{alt.name}</p>
                            {alt.reason && <p className="text-xs text-gray-500 mt-0.5 truncate">{alt.reason}</p>}
                          </div>
                          <ConfidenceBadge score={alt.confidence} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {ragAlignment?.rag_candidates_considered?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">RAG Initial Candidates</p>
                    <div className="flex flex-wrap gap-1.5">
                      {ragAlignment.rag_candidates_considered.map((c, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full border" style={{ backgroundColor: 'rgba(0,0,128,0.07)', color: '#000080', borderColor: 'rgba(0,0,128,0.15)' }}>{c}</span>
                      ))}
                    </div>
                    {ragAlignment.additional_insights && <p className="text-xs text-gray-500 mt-2 leading-relaxed">{ragAlignment.additional_insights}</p>}
                  </div>
                )}

                {missingInfo.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5 flex items-center gap-1"><Info size={11} /> Missing Information</p>
                    <ul className="space-y-1">
                      {missingInfo.map((item, i) => (
                        <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-gray-400 shrink-0 mt-1.5" />{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FraudTypeIdentifier;
